import React, { useEffect, useRef, useState } from 'react';
import { X, Upload, Loader2, CheckCircle2, AlertTriangle, FileSpreadsheet, Image as ImageIcon, Car, Tags, FolderPlus } from 'lucide-react';
import { Button } from './ui/button';
import { wooImportAPI } from '../services/api';

const Stat = ({ icon: Icon, value, label, testId }) => (
  <div className="bg-gray-50 rounded-lg p-3 text-center" data-testid={testId}>
    <Icon size={16} className="mx-auto text-orange-500 mb-1" />
    <p className="text-lg font-bold text-gray-900">{value}</p>
    <p className="text-[11px] text-gray-500">{label}</p>
  </div>
);

const WooImportModal = ({ onClose, onApplied }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [downloadImages, setDownloadImages] = useState(true);
  const [updateExisting, setUpdateExisting] = useState(true);
  const [job, setJob] = useState(null);
  const pollRef = useRef(null);

  const analyse = async (f) => {
    setFile(f); setBusy(true); setError(''); setPreview(null);
    try { setPreview(await wooImportAPI.preview(f)); } catch (e) { setError(e.response?.data?.detail || "Impossible d'analyser ce fichier"); } finally { setBusy(false); }
  };

  const apply = async () => {
    setBusy(true); setError('');
    try { setJob(await wooImportAPI.apply(preview.job_id, downloadImages, updateExisting)); } catch (e) { setError(e.response?.data?.detail || "Échec du lancement de l'import"); } finally { setBusy(false); }
  };

  useEffect(() => {
    if (!job || job.status !== 'running') return undefined;
    pollRef.current = setInterval(async () => {
      try {
        const j = await wooImportAPI.job(job.job_id || job.id);
        setJob({ ...j, job_id: j.id });
        if (j.status !== 'running') { clearInterval(pollRef.current); onApplied?.(); }
      } catch { /* ignore */ }
    }, 2000);
    return () => clearInterval(pollRef.current);
  }, [job?.status, job?.job_id, job?.id, onApplied]);

  const p = job?.progress || {};
  const pct = job?.total ? Math.round(((p.done || 0) / job.total) * 100) : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose} data-testid="woo-import-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between px-6 py-4 border-b">
          <div>
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2"><FileSpreadsheet size={20} className="text-orange-500" /> Import WooCommerce (BERU, TecDoc…)</h2>
            <p className="text-xs text-gray-500 mt-0.5">Export produits WooCommerce : SKU, catégories, descriptions, images, références OEM/équivalentes et compatibilité véhicules. Les produits créés sont <strong>non publiés</strong> avec un prix à 0 € jusqu'à saisie.</p>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-700" data-testid="woo-import-close"><X size={18} /></button>
        </div>

        <div className="p-6 overflow-y-auto space-y-4">
          {!job && (
            <label className={`flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-xl p-6 cursor-pointer transition-colors ${file ? 'border-orange-300 bg-orange-50/40' : 'border-gray-300 hover:border-orange-400'}`} data-testid="woo-import-dropzone">
              <Upload size={22} className="text-orange-500" />
              <span className="text-sm text-gray-700">{file ? file.name : 'Choisir le fichier CSV WooCommerce'}</span>
              <input type="file" accept=".csv,text/csv" className="hidden" data-testid="woo-import-file" onChange={(e) => e.target.files?.[0] && analyse(e.target.files[0])} />
            </label>
          )}
          {busy && !preview && <div className="flex justify-center py-6"><Loader2 className="animate-spin text-orange-500" /></div>}
          {error && <p className="text-sm text-red-600 flex items-center gap-2" data-testid="woo-import-error"><AlertTriangle size={16} /> {error}</p>}

          {preview && !job && (
            <>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                <Stat icon={FileSpreadsheet} value={preview.total} label="produits" testId="woo-stat-total" />
                <Stat icon={FolderPlus} value={preview.creates} label="à créer" testId="woo-stat-creates" />
                <Stat icon={Tags} value={preview.updates} label="déjà présents" testId="woo-stat-updates" />
                <Stat icon={ImageIcon} value={preview.images} label="images" testId="woo-stat-images" />
                <Stat icon={Car} value={preview.fitments.toLocaleString('fr-FR')} label="motorisations" testId="woo-stat-fitments" />
                <Stat icon={Tags} value={preview.with_oem} label="avec réf. OEM" testId="woo-stat-oem" />
              </div>
              <p className="text-xs text-gray-500">Marque(s) : <strong>{preview.brands.join(', ')}</strong> · {preview.with_alt_refs} produits avec références équivalentes d'autres marques (BOSCH, NGK…) → alimentent la recherche par référence.{preview.multi_category > 0 && <> · <strong data-testid="woo-stat-multi-category">{preview.multi_category}</strong> produits rattachés à plusieurs catégories (« Cat &gt; Sous-cat, Cat2 &gt; Sous-cat2 »).</>}</p>

              {(preview.new_categories.length > 0 || preview.new_subcategories.length > 0) && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm" data-testid="woo-new-categories">
                  <p className="font-medium text-blue-900 mb-1">Catégories à créer automatiquement</p>
                  <ul className="text-xs text-blue-800 space-y-0.5">
                    {preview.new_categories.map(c => <li key={c.name}><strong>{c.name}</strong> → {c.subcategories.join(', ') || '—'}</li>)}
                    {preview.new_subcategories.map(c => <li key={c.category}>{c.category} : nouvelles sous-catégories {c.subcategories.join(', ')}</li>)}
                  </ul>
                </div>
              )}

              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-xs" data-testid="woo-sample-table">
                  <thead className="bg-gray-50 text-gray-500 uppercase"><tr><th className="px-2 py-1.5 text-left">Réf.</th><th className="px-2 py-1.5 text-left">Nom</th><th className="px-2 py-1.5 text-left">Catégorie</th><th className="px-2 py-1.5 text-right">Img</th><th className="px-2 py-1.5 text-right">Véhic.</th><th className="px-2 py-1.5 text-right">OEM</th><th className="px-2 py-1.5 text-left">Statut</th></tr></thead>
                  <tbody className="divide-y">
                    {preview.sample.map(r => (
                      <tr key={r.ref}><td className="px-2 py-1 font-mono">{r.ref}</td><td className="px-2 py-1">{r.name}{r.type ? <span className="text-gray-400"> · {r.type}</span> : ''}</td><td className="px-2 py-1 text-gray-600">{r.category}{r.subcategory ? ` › ${r.subcategory}` : ''}{r.extra > 0 && <span className="ml-1 text-[10px] font-semibold bg-orange-50 text-orange-700 px-1 py-0.5 rounded">+{r.extra}</span>}</td><td className="px-2 py-1 text-right">{r.images}</td><td className="px-2 py-1 text-right">{r.fitments}</td><td className="px-2 py-1 text-right">{r.oem_refs}</td><td className="px-2 py-1">{r.existing ? <span className="text-amber-700">Mise à jour</span> : <span className="text-green-700">Création</span>}</td></tr>
                    ))}
                  </tbody>
                </table>
                <p className="text-[11px] text-gray-400 px-2 py-1">Aperçu des {preview.sample.length} premières lignes sur {preview.total}.</p>
              </div>

              <div className="flex flex-wrap gap-4 text-sm">
                <label className="flex items-center gap-2"><input type="checkbox" checked={downloadImages} onChange={(e) => setDownloadImages(e.target.checked)} data-testid="woo-opt-images" className="rounded" /> Télécharger et héberger les images ({preview.images})</label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={updateExisting} onChange={(e) => setUpdateExisting(e.target.checked)} data-testid="woo-opt-update" className="rounded" /> Mettre à jour les {preview.updates} produits déjà présents</label>
              </div>
            </>
          )}

          {job && (
            <div data-testid="woo-import-progress">
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="font-medium text-gray-900 flex items-center gap-2">{job.status === 'running' ? <><Loader2 size={16} className="animate-spin text-orange-500" /> Import en cours…</> : job.status === 'done' ? <><CheckCircle2 size={16} className="text-green-600" /> Import terminé</> : <><AlertTriangle size={16} className="text-red-600" /> Erreur : {job.error}</>}</span>
                <span className="text-gray-500" data-testid="woo-progress-count">{p.done || 0} / {job.total}</span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden"><div className="h-full bg-orange-500 transition-all" style={{ width: `${pct}%` }} /></div>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mt-3">
                <Stat icon={FolderPlus} value={p.created || 0} label="créés" testId="woo-prog-created" />
                <Stat icon={Tags} value={p.updated || 0} label="mis à jour" testId="woo-prog-updated" />
                <Stat icon={ImageIcon} value={p.images_ok || 0} label="images hébergées" testId="woo-prog-images" />
                <Stat icon={AlertTriangle} value={p.images_failed || 0} label="images en échec" testId="woo-prog-images-failed" />
                <Stat icon={Car} value={(p.fitments || 0).toLocaleString('fr-FR')} label="motorisations" testId="woo-prog-fitments" />
                <Stat icon={FolderPlus} value={p.categories_created || 0} label="catégories créées" testId="woo-prog-categories" />
              </div>
              {job.status === 'done' && <p className="text-xs text-gray-500 mt-3">Les nouveaux produits sont <strong>non publiés</strong> (prix 0 €). Retrouvez-les via le filtre « Non publiés » de l'onglet Produits pour saisir les prix et les publier. Vous pouvez fermer cette fenêtre, l'import continue en arrière-plan si nécessaire.</p>}
            </div>
          )}
        </div>

        <div className="px-6 py-3 border-t bg-gray-50 flex justify-end gap-2 rounded-b-xl">
          <Button variant="outline" onClick={onClose} data-testid="woo-import-cancel">{job ? 'Fermer' : 'Annuler'}</Button>
          {preview && !job && <Button onClick={apply} disabled={busy} className="bg-orange-500 hover:bg-orange-600" data-testid="woo-import-apply">{busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Upload size={16} className="mr-2" />} Importer {preview.total} produits</Button>}
        </div>
      </div>
    </div>
  );
};

export default WooImportModal;
